import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  LinearProgress,
  Alert,
  CircularProgress,
} from '@mui/material';
import { CloudUpload as CloudUploadIcon } from '@mui/icons-material';
import {
  groupRasterFiles,
  requestUploadUrls,
  uploadFilesWithProgress,
  startRasterProcessing,
} from '../api/rasterApi';
import { useRasterTileSource } from '../hooks/useRasterTileSource';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Phase = 'idle' | 'selected' | 'uploading' | 'processing' | 'complete' | 'failed';

interface GroupedFiles {
  primary: File;
  sidecars: File[];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function RasterUploadPanel() {
  const [phase, setPhase] = useState<Phase>('idle');
  const [files, setFiles] = useState<GroupedFiles | null>(null);
  const [uploadPercent, setUploadPercent] = useState(0);
  const [jobId, setJobId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);

  const { status: tileStatus, error: tileError, stepDescription } = useRasterTileSource(
    jobId ? { jobId, pollInterval: 3000 } : undefined,
  );

  // ---- Tile source status -> phase transitions ----
  useEffect(() => {
    if (phase === 'processing' && tileStatus === 'complete') {
      setPhase('complete');
    }
  }, [phase, tileStatus]);

  useEffect(() => {
    if (phase === 'processing' && tileStatus === 'failed') {
      setPhase('failed');
      setError(tileError || 'Processing failed');
    }
  }, [phase, tileStatus, tileError]);

  // ---- File selection ----
  const handleFiles = useCallback((fileList: FileList | File[]) => {
    const grouped = groupRasterFiles(fileList);
    if (!grouped) {
      setError('No raster file (.tif, .tiff, .jp2) found in selection');
      setPhase('failed');
      return;
    }
    setFiles(grouped);
    setError(null);
    setPhase('selected');
  }, []);

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        handleFiles(e.target.files);
      }
    },
    [handleFiles],
  );

  // ---- Drag and drop ----
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      if (e.dataTransfer.files.length > 0) {
        handleFiles(e.dataTransfer.files);
      }
    },
    [handleFiles],
  );

  // ---- Upload flow ----
  const handleUpload = useCallback(async () => {
    if (!files) return;

    try {
      setPhase('uploading');
      setUploadPercent(0);

      const allFiles = [files.primary, ...files.sidecars];
      const filenames = allFiles.map((f) => f.name);

      // 1. Get presigned URLs
      const { job_id, upload_urls } = await requestUploadUrls(filenames);

      // 2. Upload files with progress
      const uploads = allFiles.map((file) => {
        const entry = upload_urls.find((u) => u.filename === file.name);
        return { file, url: entry?.upload_url || '' };
      });

      await uploadFilesWithProgress(uploads, (percent) => {
        setUploadPercent(percent);
      });

      // 3. Start processing
      await startRasterProcessing(job_id);
      setJobId(job_id);
      setPhase('processing');
    } catch (e: any) {
      setError(e?.message || String(e));
      setPhase('failed');
    }
  }, [files]);

  // ---- Reset ----
  const handleReset = useCallback(() => {
    setPhase('idle');
    setFiles(null);
    setUploadPercent(0);
    setJobId(null);
    setError(null);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  }, []);

  // ---- Render ----

  // Hidden file input
  const fileInput = (
    <input
      ref={inputRef}
      type="file"
      multiple
      accept=".tif,.tiff,.tfw,.prj,.jp2,.j2w"
      style={{ display: 'none' }}
      onChange={handleFileChange}
    />
  );

  if (phase === 'idle') {
    return (
      <Box>
        {fileInput}
        <Box
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => inputRef.current?.click()}
          sx={{
            border: `2px dashed ${dragOver ? 'rgba(100,200,255,0.6)' : 'rgba(255,255,255,0.3)'}`,
            borderRadius: 3,
            p: 3,
            textAlign: 'center',
            cursor: 'pointer',
            transition: 'border-color 0.2s',
            '&:hover': { borderColor: 'rgba(255,255,255,0.5)' },
          }}
        >
          <CloudUploadIcon sx={{ fontSize: 32, color: 'rgba(255,255,255,0.5)', mb: 1 }} />
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
            Drop raster files here
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>
            or click to browse
          </Typography>
        </Box>
      </Box>
    );
  }

  if (phase === 'selected') {
    const hasSidecars = files!.sidecars.length > 0;
    return (
      <Box>
        {fileInput}
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.9)', display: 'block', mb: 0.5 }}>
          {files!.primary.name}
        </Typography>
        {files!.sidecars.map((f) => (
          <Typography key={f.name} variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', display: 'block', pl: 1 }}>
            {f.name}
          </Typography>
        ))}
        {!hasSidecars && (
          <Alert severity="warning" sx={{ mt: 1, py: 0, fontSize: '0.7rem' }}>
            No sidecar files detected. Upload may fail if the raster has no embedded georeferencing.
          </Alert>
        )}
        <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
          <Button
            variant="contained"
            size="small"
            onClick={handleUpload}
            sx={{ textTransform: 'none', flex: 1 }}
          >
            Upload
          </Button>
          <Button
            size="small"
            onClick={handleReset}
            sx={{ textTransform: 'none', color: 'rgba(255,255,255,0.6)' }}
          >
            Cancel
          </Button>
        </Box>
      </Box>
    );
  }

  if (phase === 'uploading') {
    return (
      <Box>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mb: 1 }}>
          Uploading... {uploadPercent}%
        </Typography>
        <LinearProgress
          variant="determinate"
          value={uploadPercent}
          sx={{ borderRadius: 4 }}
        />
      </Box>
    );
  }

  if (phase === 'processing') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <CircularProgress size={20} sx={{ color: 'rgba(100,200,255,0.8)' }} />
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
          {stepDescription || 'Processing...'}
        </Typography>
      </Box>
    );
  }

  if (phase === 'complete') {
    return (
      <Box>
        <Alert severity="success" sx={{ py: 0, fontSize: '0.8rem' }}>
          Raster added to globe
        </Alert>
        <Button
          size="small"
          onClick={handleReset}
          sx={{ textTransform: 'none', color: 'rgba(255,255,255,0.6)', mt: 1 }}
        >
          Upload Another
        </Button>
      </Box>
    );
  }

  // phase === 'failed'
  return (
    <Box>
      <Alert severity="error" sx={{ py: 0, fontSize: '0.8rem' }}>
        {error || 'An unknown error occurred'}
      </Alert>
      <Button
        size="small"
        onClick={handleReset}
        sx={{ textTransform: 'none', color: 'rgba(255,255,255,0.6)', mt: 1 }}
      >
        Try Again
      </Button>
    </Box>
  );
}
